import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter, Button } from '../../../components/ui';
import { useFlexibleList, fetcher } from '../../../hooks/useSWR';

const ComplaintTypeSelector = ({ isOpen, onClose, onSelectType, config }) => {
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [documentsByType, setDocumentsByType] = useState({});
  
  // Fetch complaint types
  const { data: complaintTypes = [] } = useFlexibleList(
    config?.api?.lookupEndpoints?.complaintTypes || '/complaint-type'
  );

  // Map complaint type names to module names
  // GDP -> COMPLAINT_GDP, GMP -> COMPLAINT_GMP
  const getModuleNameForType = (typeName) => {
    const mapping = {
      'GDP': 'COMPLAINT_GDP',  // For GDP complaints, fetch documents where moduleId = 'COMPLAINT_GDP'
      'GMP': 'COMPLAINT_GMP'   // For GMP complaints, fetch documents where moduleId = 'COMPLAINT_GMP'
    };
    return mapping[typeName?.toUpperCase()] || null;
  };

  // Fetch all document modules to find the correct names
  const { data: allDocumentModules = [] } = useFlexibleList('/document-module');

  // Fetch documents for each complaint type when modal opens
  useEffect(() => {
    if (isOpen && complaintTypes.length > 0 && allDocumentModules.length > 0) {
      const fetchDocuments = async () => {
        const docsByType = {};
        
        // Debug: Log all available modules
        console.log('Available DocumentModules:', allDocumentModules.map(m => ({ id: m.id, name: m.name })));
        
        for (const type of complaintTypes) {
          const expectedModuleName = getModuleNameForType(type.name);
          if (expectedModuleName) {
            // Try to find the module - first by exact name, then by partial match
            let module = allDocumentModules.find(m => 
              m.name === expectedModuleName || 
              m.name?.toUpperCase() === expectedModuleName.toUpperCase() ||
              m.name?.includes('GDP') && type.name.toUpperCase() === 'GDP' ||
              m.name?.includes('GMP') && type.name.toUpperCase() === 'GMP'
            );
            
            if (module) {
              try {
                console.log(`Fetching documents for ${type.name} with module: "${module.name}" (id: ${module.id})`);
                const documents = await fetcher(`/document-management/document?module=${encodeURIComponent(module.name)}`);
                const docsArray = Array.isArray(documents) ? documents : [];
                console.log(`Fetched documents for ${type.name} (${module.name}):`, docsArray.length, docsArray);
                docsByType[type.id] = docsArray;
              } catch (error) {
                console.error(`Failed to fetch documents for ${module.name}:`, error);
                docsByType[type.id] = [];
              }
            } else {
              console.warn(`No DocumentModule found for ${type.name}. Expected: "${expectedModuleName}"`);
              console.warn('Available modules:', allDocumentModules.map(m => m.name));
              docsByType[type.id] = [];
            }
          }
        }
        setDocumentsByType(docsByType);
      };
      fetchDocuments();
    }
  }, [isOpen, complaintTypes, allDocumentModules]);

  // Reset selection when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedTypeId(null);
      setDocumentsByType({});
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedTypeId) {
      const selectedType = complaintTypes.find(t => t.id === selectedTypeId);
      onSelectType(selectedTypeId, selectedType);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader>
        <ModalTitle>Select Complaint Type</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Please select the type of complaint you want to create:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {complaintTypes
              .filter(type => type.isActive !== false)
              .map((type) => {
                const typeDocuments = documentsByType[type.id] || [];
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setSelectedTypeId(type.id)}
                    className={`p-6 border-2 rounded-lg transition-all text-left ${
                      selectedTypeId === type.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500 ring-opacity-50'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {type.name}
                  </div>
                  
                    {/* Display document details in each box - show only the first document */}
                    {typeDocuments.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Reference Document:
                        </div>
                        <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                          {typeDocuments[0].code} {typeDocuments[0].currentRevision?.version ? `- ${typeDocuments[0].currentRevision.version}` : ''}
                        </div>
                      </div>
                    )}
                    
                    {typeDocuments.length === 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                          No reference document found
                        </div>
                      </div>
                    )}
                    
                    {selectedTypeId === type.id && (
                      <div className="mt-3 text-sm text-blue-600 dark:text-blue-400 font-medium">
                        ✓ Selected
                      </div>
                    )}
                  </button>
                );
              })}
          </div>
          
          {complaintTypes.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No complaint types available. Please contact your administrator.
            </div>
          )}
        </div>
      </ModalContent>
      <ModalFooter>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedTypeId}>
            Continue
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default ComplaintTypeSelector;

